import { z } from 'zod';

import {
  parseDraftPresentation,
  resolveDraftPresentation,
  type DraftPresentation,
} from '../../contracts/approval-kinds';
import { isRecord } from '../../contracts/parse-utils';
import type { ApprovalContext } from '../../contracts/tool-approval.types';

/**
 * Server-built facts an approval card shows that the model didn't write:
 * `context.presentation` (ml-scribe `Presentation`), the tool's fixed title and
 * its facts as typed blocks. A tool sends only the blocks that apply. Values
 * are raw, so the card formats.
 */
const fieldValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

const patientBlockSchema = z.object({
  kind: z.literal('patient'),
  id: z.string().nullish(),
  display_name: z.string(),
});

const sessionBlockSchema = z.object({
  kind: z.literal('session'),
  id: z.string().nullish(),
  title: z.string().nullish(),
  starts_at: z.string().nullish(),
  timezone: z.string().nullish(),
});

const ehrBlockSchema = z.object({
  kind: z.literal('ehr'),
  provider: z.string(),
  display_name: z.string(),
});

const textChangeBlockSchema = z.object({
  kind: z.literal('text_change'),
  target: z.enum(['consult_note', 'session_context', 'session_document']),
  before: z.string(),
  after: z.string(),
});

const fieldChangeBlockSchema = z.object({
  kind: z.literal('field_change'),
  target: z.literal('patient_profile'),
  field: z.string(),
  before: fieldValueSchema,
  after: fieldValueSchema,
});

const emailAttachmentSchema = z.object({
  filename: z.string().trim().min(1),
  media_type: z.string().catch(''),
  size_bytes: z.number().finite().nonnegative(),
  /** The sealed file in the approval's run; a row without one is listed but cannot open. */
  attachment_id: z.string().trim().min(1).optional().catch(undefined),
});

export type EmailAttachment = z.infer<typeof emailAttachmentSchema>;

/** Each file parses on its own: an invalid one is dropped, so a short list fails the mail card's check. */
const emailAttachmentsSchema = z
  .array(z.unknown())
  .catch([])
  .transform((rows) =>
    rows.flatMap((row) => {
      const parsed = emailAttachmentSchema.safeParse(row);
      return parsed.success ? [parsed.data] : [];
    })
  );

const MAIL_PROVIDER_LABELS: Readonly<Record<string, string>> = {
  gmail: 'Gmail',
  'google-mail': 'Gmail',
  outlook: 'Outlook',
  microsoft: 'Outlook',
};

const trimmed = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/** A blank label takes the provider's display name; an unknown provider's is left to the card to translate. */
const mailSenderLabel = (provider: string, label: unknown): string | null =>
  trimmed(label) || MAIL_PROVIDER_LABELS[provider] || null;

/** The mailbox a connected-mail call sends from. No address means the provider is known but its address could not be read. */
const emailSenderSchema = z
  .object({
    provider: z.string().trim().min(1),
    label: z.string().nullish(),
    address: z
      .string()
      .trim()
      .nullish()
      .transform((address) => address || null),
  })
  .transform(({ provider, label, address }) => ({
    provider,
    label: mailSenderLabel(provider, label),
    address,
  }));

export type EmailSender = z.infer<typeof emailSenderSchema>;

const emailBlockSchema = z.object({
  kind: z.literal('email'),
  to: z.array(z.string()).default([]),
  sender: emailSenderSchema.nullish().catch(null),
  subject: z.string(),
  content: z.string().nullish(),
  has_attachment: z.boolean().default(false),
  attachments: emailAttachmentsSchema,
});

const mergeCandidatesBlockSchema = z.object({
  kind: z.literal('merge_candidates'),
  target: z.enum(['patient_profiles', 'sessions']),
  rows: z.array(
    z.object({
      id: z.string(),
      name: z.string().optional(),
      date_of_birth: z.string().optional(),
      title: z.string().optional(),
      started_at: z.string().optional(),
    })
  ),
});

const notePushRowsBlockSchema = z.object({
  kind: z.literal('note_push_rows'),
  rows: z
    .array(
      z.object({
        selector: z.string(),
        section_headings: z.array(z.string()),
        prepared_input: z.string(),
      })
    )
    .min(1),
});

const blockSchema = z.discriminatedUnion('kind', [
  patientBlockSchema,
  sessionBlockSchema,
  ehrBlockSchema,
  textChangeBlockSchema,
  fieldChangeBlockSchema,
  emailBlockSchema,
  mergeCandidatesBlockSchema,
  notePushRowsBlockSchema,
]);

export type PatientBlock = z.infer<typeof patientBlockSchema>;
export type SessionBlock = z.infer<typeof sessionBlockSchema>;
export type EhrBlock = z.infer<typeof ehrBlockSchema>;
export type TextChangeBlock = z.infer<typeof textChangeBlockSchema>;
export type FieldChangeBlock = z.infer<typeof fieldChangeBlockSchema>;
export type EmailBlock = z.infer<typeof emailBlockSchema>;
export type MergeCandidatesBlock = z.infer<typeof mergeCandidatesBlockSchema>;
export type NotePushRowsBlock = z.infer<typeof notePushRowsBlockSchema>;
export type PresentationBlock = z.infer<typeof blockSchema>;

export interface ApprovalPresentation {
  /** The tool's fixed approval title, translated server-side; never model-written. */
  title?: string;
  blocks: PresentationBlock[];
}

const titleSchema = z.string().min(1);

/** Each block parses on its own, so an unknown or malformed block never hides the rest. */
const parseBlocks = (raw: readonly unknown[]): PresentationBlock[] =>
  raw.flatMap((block) => {
    const parsed = blockSchema.safeParse(block);
    return parsed.success ? [parsed.data] : [];
  });

export const blocksOfKind = <K extends PresentationBlock['kind']>(
  blocks: readonly PresentationBlock[],
  kind: K
): Extract<PresentationBlock, { kind: K }>[] =>
  blocks.filter(
    (block): block is Extract<PresentationBlock, { kind: K }> => block.kind === kind
  );

const TEXT_CHANGE_TARGETS = ['consult_note', 'session_context', 'session_document'];
const MERGE_TARGETS = ['patient_profiles', 'sessions'];

const recordOf = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? value : {};

const keyedBlock = (
  kind: PresentationBlock['kind'],
  raw: unknown,
  fields: Record<string, unknown> = {}
): unknown => (isRecord(raw) ? { ...raw, ...fields, kind } : undefined);

const isMailboxAddress = (value: string): boolean =>
  value.length <= 320 && value.includes('@') && !/\s/.test(value);

/**
 * An older mail preview's sender. An empty address is a label-only sender: the
 * mailbox could not be read, and send still uses that provider. A value that is
 * not an address is no sender.
 */
const legacySender = (raw: unknown): EmailSender | undefined => {
  if (!isRecord(raw)) return undefined;
  const provider = trimmed(raw.provider);
  if (!provider) return undefined;
  const address = trimmed(raw.address);
  if (address && !isMailboxAddress(address)) return undefined;
  return {
    provider,
    label: mailSenderLabel(provider, raw.label),
    address: address || null,
  };
};

/** An older preview's file opens through `open`; only a run attachment carries over. */
const legacyAttachments = (raw: unknown): unknown =>
  Array.isArray(raw)
    ? raw.map((row: unknown) =>
        isRecord(row) && isRecord(row.open) && row.open.kind === 'run-attachment'
          ? { ...row, attachment_id: row.open.attachment_id }
          : row
      )
    : raw;

const previewBlock = (
  preview: Record<string, unknown>,
  draftTarget: DraftPresentation['target'] | undefined
): unknown => {
  if (preview.kind === 'text_edit') {
    return {
      kind: 'text_change',
      target: draftTarget,
      before: preview.before,
      after: preview.after,
    };
  }
  if (preview.kind !== undefined && preview.kind !== 'email') return undefined;
  const mail = {
    sender: legacySender(preview.sender),
    attachments: legacyAttachments(preview.attachments),
  };
  if (preview.kind === 'email') return { ...preview, ...mail };
  // An older preview has no kind: a mail reply's names who the reply goes to and
  // the thread subject; a session email's is the subject and body it sends; a
  // mail send's or draft's is only the mailbox and the files.
  const address = trimmed(preview.recipient_address);
  if (address) {
    const name = trimmed(preview.recipient_name);
    return {
      kind: 'email',
      to: [name ? `${name} <${address}>` : address],
      subject: typeof preview.subject === 'string' ? preview.subject : '',
      ...mail,
    };
  }
  if (preview.sender === undefined && preview.attachments === undefined) {
    return { ...preview, kind: 'email' };
  }
  return { subject: '', ...preview, ...mail, kind: 'email' };
};

/**
 * Blocks for a context written before `presentation.blocks`: the keyed
 * presentation, `approval_preview`, `merge_approval` and `note_push_approval`.
 * Cards persisted then still render, and nothing else reads those keys. A text
 * edit's preview names no target, so it takes the call's draft target, which
 * `toolName` resolves when the context carries no `draft`.
 */
export const legacyBlocks = (
  context: Record<string, unknown> | undefined,
  toolName?: string
): PresentationBlock[] => {
  const keyed = recordOf(context?.presentation);
  const changes = recordOf(recordOf(keyed.patient_profile).changes);
  const merge = recordOf(context?.merge_approval);
  return parseBlocks([
    keyedBlock('patient', keyed.patient),
    keyedBlock('session', keyed.session),
    ...TEXT_CHANGE_TARGETS.map((target) =>
      keyedBlock('text_change', keyed[target], { target })
    ),
    ...Object.entries(changes).map(([field, change]) =>
      keyedBlock('field_change', change, { target: 'patient_profile', field })
    ),
    previewBlock(
      recordOf(context?.approval_preview),
      (toolName
        ? resolveDraftPresentation(toolName, context?.draft)
        : parseDraftPresentation(context?.draft)
      )?.target
    ),
    ...MERGE_TARGETS.map((target) => ({
      kind: 'merge_candidates',
      target,
      rows: merge[target],
    })),
    { kind: 'note_push_rows', rows: context?.note_push_approval },
  ]);
};

/** `context.presentation`, with the old shape read through `legacyBlocks` when it has no blocks. */
export const readPresentation = (
  context: ApprovalContext | undefined,
  toolName?: string
): ApprovalPresentation => {
  const raw = recordOf(context?.presentation);
  const title = titleSchema.safeParse(raw.title);
  return {
    ...(title.success ? { title: title.data } : {}),
    blocks: Array.isArray(raw.blocks)
      ? parseBlocks(raw.blocks)
      : legacyBlocks(context, toolName),
  };
};
